'use client'

import { useLanguage, useTranslate } from '@repo/shared/hooks'
import { type FormSchema, useAppForm } from '@repo/ui/hooks'

import { useAddCardSchema } from '@repo/shared/schemas'
import { useAddShebaSchema } from '@repo/shared/schemas'
import { useCreateBank } from '@repo/services'
import {
  FormField,
  Form,
  FormItemWrapper,
  Button,
  ModalTitle,
  ModalHeader,
  onCloseModal,
  InputNumber,
  Typography,
} from '@repo/ui/components'
import { useErrorHandler } from '@repo/shared/hooks'

export const ShebaInput = () => {
  const { t } = useTranslate()
  const language = useLanguage()
  const { addCardSchema } = useAddCardSchema()
  const { addShebaSchema } = useAddShebaSchema()
  const { errorHandler } = useErrorHandler()
  const { form: cardForm } = useAppForm({
    formSchema: addCardSchema,
    defaultValues: {
      addSheba: true,
    },
  })

  const { form: shebaForm } = useAppForm({
    formSchema: addShebaSchema,
    defaultValues: {},
  })

  const createBankMutation = useCreateBank({ invalidate: true })

  const onSubmit = async (data: FormSchema<typeof addShebaSchema>) => {
    cardForm.clearErrors()

    createBankMutation.mutate(
      { type: 'iban', value: `IR${data.shebaNumber}` },
      {
        onError: (error) => {
          const message = errorHandler(error, {
            type: 'both',
          })
          shebaForm.setError('shebaNumber', {
            message,
          })
        },
        onSuccess: () => {
          shebaForm.reset()
          onCloseModal()
        },
      },
    )
  }

  return (
    <>
      <ModalHeader>
        <ModalTitle />
      </ModalHeader>

      <Form {...shebaForm}>
        <form
          onSubmit={(e) => {
            e.stopPropagation()
            shebaForm.handleSubmit(onSubmit)(e)
          }}
          className='space-y-8 w-full'
          id='sheba-input-form'
        >
          <FormField
            control={shebaForm.control}
            name='shebaNumber'
            render={({ field }) => (
              <FormItemWrapper
                title={t('addCardPage_shebaNumber')}
                caption={t('addCardPage_cardNumber_sheba_caption')}
                inputSize='md'
              >
                <InputNumber
                  startIcon={
                    language !== 'fa' ? (
                      <Typography
                        variant='label'
                        weight='regular'
                        size='sm'
                        color='text-neutral-surface-80'
                      >
                        IR
                      </Typography>
                    ) : undefined
                  }
                  endIcon={
                    language === 'fa' ? (
                      <Typography
                        variant='label'
                        weight='regular'
                        size='sm'
                        color='text-neutral-surface-80'
                      >
                        IR
                      </Typography>
                    ) : undefined
                  }
                  className='ltr'
                  type='text'
                  autoComplete='off'
                  {...field}
                  inputSize='md'
                  autoFocus
                />
              </FormItemWrapper>
            )}
          />

          <Button
            className='w-full'
            variant='fill'
            size='lg'
            form='sheba-input-form'
            type='submit'
            loading={createBankMutation.isPending}
          >
            {t('addCardPage_submit')}
          </Button>
        </form>
      </Form>
    </>
  )
}
